import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { AM_TP_DEMO_ROUTES } from './types';

/**
 * SWR key prefixes the demo mutations move.
 *
 * These are the *product's* keys, not this folder's: the demo writes real rows, and the screens that read them
 * back are the real transfer pricing screens. Invalidating only the demo's own key would leave every one of
 * them serving a cached response from before the write, which is exactly the class of bug where someone
 * "has to refresh to see it".
 *
 * Each entry is the URL its hook builds, so a rename is greppable:
 *   transfer-pricing/policy?     — `usePolicies`, the policy agreements table
 *   tp-document-record?          — `useTPDocumentRecords`, the local & master file screen
 *   transfer-pricing/benchmark?  — `useTransferPricingBenchmarks`, the benchmarking screen
 *   tp-alert?                    — `useTPAlerts`, the alerts screen
 *   document-template            — the template library
 *   demo/client-overview?        — `useAmTpDemoClientOverview`, the client profile stat row
 *
 * Prefixes rather than exact keys, because several of those URLs carry query strings that vary per caller —
 * `transfer-pricing/policy?companyId=42&serviceProviderEntityIds=…` is a different key from
 * `transfer-pricing/policy?companyId=42`, and an exact match would miss half the mounted hooks.
 */
const AM_TP_DEMO_STALE_KEY_PREFIXES = [
  'transfer-pricing/policy',
  'tp-document-record',
  'transfer-pricing/benchmark',
  'tp-alert',
  'document-template',
  AM_TP_DEMO_ROUTES.clientOverview,
  // The version chips, the rail and the document preview all read this; without it the rail keeps showing the
  // chain from before a roll-forward wrote to it.
  AM_TP_DEMO_ROUTES.policyVersions,
  // The demo's own template library. `document-template` above is the *product's* key, which this list does not
  // share — so without this the Doc Gen picker keeps serving the library from before the template save.
  AM_TP_DEMO_ROUTES.templateList,
] as const;

/**
 * Revalidates every read a demo mutation can invalidate.
 *
 * Call it after any of the five mutations resolves successfully. It is deliberately one function rather than
 * one per mutation: the fan-out is wide, the cost of revalidating an unaffected key is a single background
 * request, and the cost of forgetting one is a screen that silently contradicts the one before it.
 */
export function useInvalidateAmTpDemo() {
  const { mutate } = useSWRConfig();

  return useCallback(() => {
    // Called with no data argument on purpose: SWR then revalidates in the background while keeping the current
    // data on screen. Passing `undefined` would clear the cache and flash an empty table mid-demo.
    void mutate(
      (key: unknown) =>
        typeof key === 'string' &&
        AM_TP_DEMO_STALE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))
    );
  }, [mutate]);
}
